import React, { useEffect, useMemo, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { Button, Input, Popover, Tooltip, notification } from "antd";
import { LockOutlined, PlusOutlined, SearchOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { DEFAULT_VISIBILITY, VisibilityChoice, visibilityOf } from "../../../routes/savedReplies/visibility";
import axios from "axios";
import "./SelectCanned.less";

const SEARCH_FROM = 6;

// Saved replies: ready texts added to the message. Search when there are many; a new one can be added here
// (starting from the typed message), private (only me) or public (the whole team).
const SelectCanned = ({intl, handleAutoComplete, cannedResponses, currentText = "", onSaved}) => {
    const t = (id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [naming, setNaming] = useState(false);
    const [name, setName] = useState("");
    const [draftText, setDraftText] = useState("");
    const [visibility, setVisibility] = useState(DEFAULT_VISIBILITY);
    const [saving, setSaving] = useState(false);
    const searchRef = useRef(null);
    const panelRef = useRef(null);
    const list = useMemo(() => cannedResponses || [], [cannedResponses]);

    const shown = useMemo(() => {
        const text = query.trim().toLowerCase();
        if (!text) return list;
        return list.filter(item => `${item.name} ${item.text}`.toLowerCase().includes(text));
    }, [list, query]);

    useEffect(() => {
        if (!open) return undefined;
        setQuery("");
        setActive(0);
        setNaming(false);
        setName("");
        // Focus the search, or the list itself when there is none, so arrows and Enter work straight away.
        const timer = setTimeout(() => {
            const target = searchRef.current || panelRef.current;
            if (target && target.focus) target.focus();
        }, 60);
        return () => clearTimeout(timer);
    }, [open]);

    useEffect(() => setActive(0), [query]);

    const pick = item => {
        handleAutoComplete(item.text);
        setOpen(false);
    };

    const startNew = () => {
        setName("");
        setDraftText(currentText);
        setVisibility(DEFAULT_VISIBILITY);
        setNaming(true);
    };

    const save = async () => {
        if (!name.trim() || !draftText.trim()) return;
        setSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/canned-responses`, {name: name.trim(), text: draftText.trim(), visibility});
            notification.success({message: t("canned.saved", "Saved reply “{name}” added", {name: name.trim()})});
            onSaved && onSaved();
            setNaming(false);
            setName("");
        } catch (error) {
            notification.error({message: t("canned.saveFailed", "Could not save the reply. Please try again.")});
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = event => {
        if (naming) return;
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!shown.length) return;
            setActive((active + (event.key === "ArrowDown" ? 1 : -1) + shown.length) % shown.length);
        } else if (event.key === "Enter" && shown[active]) {
            event.preventDefault();
            pick(shown[active]);
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    };

    const panel = (
        <div className="canned-picker" onKeyDown={onKeyDown} ref={panelRef} tabIndex={-1}>
            {list.length >= SEARCH_FROM ? (
                <div className="canned-picker__head">
                    <Input
                        ref={searchRef}
                        allowClear
                        prefix={<SearchOutlined/>}
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder={t("canned.search", "Search saved replies")}
                        aria-label={t("canned.search", "Search saved replies")}
                    />
                </div>
            ) : (
                <div className="canned-picker__title">{t("canned.title", "Saved replies")}</div>
            )}
            <div className="canned-picker__list" role="listbox">
                {shown.map((item, index) => (
                    <div
                        key={item._id || index}
                        role="option"
                        aria-selected={active === index}
                        className={`canned-picker__row${active === index ? " is-active" : ""}`}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => pick(item)}
                    >
                        <strong>
                            {item.name}
                            {visibilityOf(item) === "private" ? <LockOutlined className="canned-picker__lock" aria-label={t("savedReplies.privateShort", "Private")}/> : null}
                        </strong>
                        <span>{item.text}</span>
                    </div>
                ))}
                {!list.length && !naming ? (
                    <div className="canned-picker__empty">
                        <strong>{t("canned.none", "No saved replies")}</strong>
                        <span>{t("canned.emptyText", "Save a message you send often to add it with one click next time.")}</span>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={startNew}>{t("savedReplies.new", "New saved reply")}</Button>
                    </div>
                ) : !shown.length ? (
                    <div className="canned-picker__empty"><strong>{t("canned.noMatch", "No saved reply matches “{query}”", {query})}</strong></div>
                ) : null}
            </div>
            {naming ? (
                <form className="canned-picker__form" onSubmit={event => { event.preventDefault(); save(); }}>
                    <Input
                        autoFocus
                        value={name}
                        maxLength={60}
                        onChange={event => setName(event.target.value)}
                        placeholder={t("canned.namePlaceholder", "Name, e.g. Opening hours")}
                        aria-label={t("canned.name", "Name")}
                    />
                    <Input.TextArea
                        value={draftText}
                        maxLength={2000}
                        autoSize={{minRows: 3, maxRows: 8}}
                        onChange={event => setDraftText(event.target.value)}
                        placeholder={t("canned.textPlaceholder", "The reply")}
                        aria-label={t("savedReplies.text", "Reply")}
                    />
                    <VisibilityChoice intl={intl} size="small" value={visibility} onChange={setVisibility}/>
                    <div className="canned-picker__form-actions">
                        <Button size="small" onClick={() => setNaming(false)}>{t("button.cancel", "Cancel")}</Button>
                        <Button size="small" type="primary" htmlType="submit" loading={saving} disabled={!name.trim() || !draftText.trim()}>{t("save", "Save")}</Button>
                    </div>
                </form>
            ) : list.length ? (
                <div className="canned-picker__foot">
                    <button type="button" className="canned-picker__add" onClick={startNew}>
                        <PlusOutlined/> {currentText.trim() ? t("canned.saveTyped", "Save the typed message as a reply") : t("savedReplies.new", "New saved reply")}
                    </button>
                </div>
            ) : null}
        </div>
    );

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            placement="topLeft"
            arrow={false}
            overlayClassName="canned-picker-popover"
            content={panel}
            destroyTooltipOnHide
        >
            <Tooltip title={open ? null : t("canned.title", "Saved replies")}>
                <button type="button" className="canned-trigger" aria-label={t("canned.title", "Saved replies")} aria-haspopup="listbox" aria-expanded={open}>
                    <ThunderboltOutlined/>
                </button>
            </Tooltip>
        </Popover>
    );
};

export default injectIntl(SelectCanned);
